import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../app/AppContext';
import { Notice } from '../components/Notice';
import type { ModuleId } from '../core/types';
import {
  MINIMUM_VIEWPORT,
  PROTOCOL_VERSION,
  createEnvironmentSnapshot,
  createId,
  getEnvironmentConditionId,
  getSessionEnvironmentConditionId,
  hashSeed,
  sampleFrameDiagnostics,
  visualDegreesToPixels,
} from '../core';
import { MODULE_INFO } from '../training';
import { trainingStimuliFitViewport } from '../training/display-fit';

const MODULE_IDS: readonly ModuleId[] = ['read-switch', 'central-peripheral', 'player-track', 'moving-target'];

function isModuleId(value: string | null): value is ModuleId {
  return MODULE_IDS.includes(value as ModuleId);
}

function sameModules(left: readonly ModuleId[], right: readonly ModuleId[]): boolean {
  return left.length === right.length && left.every((moduleId) => right.includes(moduleId));
}

export function SessionSetupPage() {
  const app = useApp();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const requestedModule = params.get('module');
  const selectedModule = isModuleId(requestedModule) ? requestedModule : undefined;
  const selectedModules = selectedModule ? [selectedModule] : [...MODULE_IDS];
  const copy = selectedModule
    ? {
        code: '1課題だけ練習',
        title: MODULE_INFO[selectedModule].label,
        description: `${MODULE_INFO[selectedModule].description}結果に応じて難しさが少しずつ変わります。`,
        duration: MODULE_INFO[selectedModule].durationLabel,
      }
    : {
        code: '4課題をまとめて練習',
        title: '見る・追う・素早く答える',
        description: '4つの課題を順番に行います。各課題の結果に応じて難しさが変わります。',
        duration: '約12分',
      };
  const [fatigue, setFatigue] = useState<1 | 2 | 3 | 4 | 5>(3);
  const [correctionConfirmed, setCorrectionConfirmed] = useState(false);
  const [fullscreenError, setFullscreenError] = useState(false);
  const [environmentError, setEnvironmentError] = useState<string>();
  const [checkingEnvironment, setCheckingEnvironment] = useState(false);
  const [viewport, setViewport] = useState(() => ({ width: window.innerWidth, height: window.innerHeight, dpr: window.devicePixelRatio }));

  useEffect(() => {
    const update = () => setViewport({ width: window.innerWidth, height: window.innerHeight, dpr: window.devicePixelRatio });
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const viewportReady = viewport.width >= MINIMUM_VIEWPORT.width && viewport.height >= MINIMUM_VIEWPORT.height;
  const dprReady = !app.environment || Math.abs(viewport.dpr - app.environment.devicePixelRatio) <= 0.05;
  const pixelsPerDegree = app.calibration
    ? visualDegreesToPixels(1, app.calibration.viewingDistanceCm, app.calibration.pixelsPerCentimeter)
    : Number.POSITIVE_INFINITY;
  const visualFieldReady = trainingStimuliFitViewport(viewport.width, viewport.height, pixelsPerDegree);
  const activeSession = app.sessions.find((session) => session.status === 'in-progress'
    && session.mode === 'training'
    && sameModules(session.moduleIds, selectedModules)
    && app.environment
    && getSessionEnvironmentConditionId(session) === getEnvironmentConditionId(app.environment)
    && session.protocolVersion === PROTOCOL_VERSION);
  const setupReady = Boolean(app.calibration && app.environment && correctionConfirmed && dprReady && !checkingEnvironment);
  const environmentText = useMemo(() => {
    if (!app.environment || !app.calibration) return '準備が必要です';
    return `${Math.round(app.environment.refreshRateHz)} Hz · ${app.calibration.viewingDistanceCm} cm · ${app.environment.viewportWidth}×${app.environment.viewportHeight}`;
  }, [app.environment, app.calibration]);

  const begin = async () => {
    if (!setupReady) return;
    setFullscreenError(false);
    setEnvironmentError(undefined);
    try {
      if (!document.fullscreenEnabled) throw new Error('fullscreen unavailable');
      if (!document.fullscreenElement) await document.documentElement.requestFullscreen();
    } catch {
      setFullscreenError(true);
      return;
    }
    if (!document.fullscreenElement) { setFullscreenError(true); return; }
    setCheckingEnvironment(true);
    try {
      await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
      const width = window.innerWidth;
      const height = window.innerHeight;
      const dpr = window.devicePixelRatio;
      if (width < MINIMUM_VIEWPORT.width || height < MINIMUM_VIEWPORT.height) {
        throw new Error(`全画面でも${MINIMUM_VIEWPORT.width}×${MINIMUM_VIEWPORT.height}pxを確保できません。`);
      }
      if (!trainingStimuliFitViewport(width, height, pixelsPerDegree)) {
        throw new Error('現在の画面と距離では、課題を正しい大きさで画面内に表示できません。「測定の準備」を確認してください。');
      }
      const diagnostics = await sampleFrameDiagnostics({ sampleCount: 90 });
      if (!diagnostics.stable) {
        throw new Error('画面の動きが安定していません。ほかのタブや負荷の高いアプリを閉じてください。');
      }
      const conditionKey = [
        app.calibration!.id,
        `${width}x${height}`,
        dpr.toFixed(3),
        Math.round(diagnostics.estimatedRefreshRateHz),
        navigator.userAgent,
      ].join('|');
      const conditionId = `condition-${hashSeed(conditionKey).toString(16)}`;
      const nextEnvironment = createEnvironmentSnapshot(diagnostics, {
        id: createId('env-session'),
        conditionId,
        calibrationId: app.calibration!.id,
        viewportWidth: width,
        viewportHeight: height,
        devicePixelRatio: dpr,
        userAgent: navigator.userAgent,
      });
      const resumeSession = app.sessions.find((session) => session.status === 'in-progress'
        && session.mode === 'training'
        && sameModules(session.moduleIds, selectedModules)
        && getSessionEnvironmentConditionId(session) === conditionId
        && session.protocolVersion === PROTOCOL_VERSION);
      if (resumeSession) await app.activateStoredEnvironment(resumeSession.environmentId);
      else await app.activateEnvironment(nextEnvironment);
      const moduleQuery = selectedModule ? `&module=${selectedModule}` : '';
      const resumeQuery = resumeSession ? `&resume=${encodeURIComponent(resumeSession.id)}` : '';
      navigate(`/train?mode=training&fatigue=${fatigue}&correction=1${moduleQuery}${resumeQuery}`);
    } catch (error) {
      setEnvironmentError(error instanceof Error ? error.message : '表示環境を確認できませんでした。');
      setCheckingEnvironment(false);
      if (document.fullscreenElement) await document.exitFullscreen().catch(() => undefined);
    }
  };

  if (!app.calibration || !app.environment) {
    return <div className="page"><div className="empty-state"><div><strong>先に「測定の準備」を行ってください。</strong><p>画面の大きさと見る位置を登録してから始めます。</p><Link className="button" to="/calibration">測定の準備へ</Link></div></div></div>;
  }

  return (
    <div className="page">
      <header className="page-heading"><div><div className="eyebrow">練習モード</div><h1>今回の課題と状態を確認します</h1><p>練習する課題は毎回自由に変えられます。</p></div></header>
      <nav className="practice-mode-picker" aria-label="練習する課題">
        <Link className={!selectedModule ? 'active' : ''} to="/session"><strong>4課題すべて</strong><small>約12分</small></Link>
        {MODULE_IDS.map((moduleId) => <Link key={moduleId} className={selectedModule === moduleId ? 'active' : ''} to={`/session?module=${moduleId}`}><strong>{MODULE_INFO[moduleId].shortLabel}</strong><small>{MODULE_INFO[moduleId].durationLabel}</small></Link>)}
      </nav>
      <div className="session-setup-grid">
        <section className="panel">
          <div className="panel-label">今回の内容</div>
          <div className="session-mode-heading"><span>{copy.code}</span><strong>{copy.title}</strong><small>{copy.duration}</small></div>
          <p className="session-description">{copy.description}</p>
          <div className="session-block-list">
            {selectedModules.map((moduleId, index) => <div key={moduleId}><span>{index + 1}</span><strong>{MODULE_INFO[moduleId].label}</strong><small>{MODULE_INFO[moduleId].primaryAction}</small></div>)}
          </div>
          <Notice title="全画面表示で開始します。">途中でタブを変える、画面サイズを変える、全画面を解除すると現在の試行は無効になります。</Notice>
          {activeSession && <Notice title="未完了セッションがあります。" tone="warning">保存済みの {activeSession.trialIds.length} 試行の続きから再開します。</Notice>}
        </section>
        <section className="panel">
          <div className="panel-label">体調と環境</div><h2>実施前の状態</h2>
          <fieldset className="fatigue-field"><legend>現在の疲労感</legend><div className="fatigue-scale">{([1, 2, 3, 4, 5] as const).map((value) => <label key={value}><input type="radio" name="fatigue" value={value} checked={fatigue === value} onChange={() => setFatigue(value)} /><span>{value}</span><small>{value === 1 ? '低' : value === 5 ? '高' : ''}</small></label>)}</div></fieldset>
          <label className="confirmation"><input type="checkbox" checked={correctionConfirmed} onChange={(event) => setCorrectionConfirmed(event.target.checked)} /><span><strong>眼鏡・コンタクトを確認</strong>「測定の準備」をしたときと同じ状態です。</span></label>
          <div className="environment-check"><span className="panel-label">登録済みの条件</span><strong>{environmentText}</strong><small className={viewportReady && visualFieldReady ? 'quality-good' : undefined}>開始後、課題が画面内に収まるか全画面で確認します</small></div>
          {!dprReady && <Notice title="表示倍率またはモニターが前回と異なります。" tone="danger">ブラウザの表示倍率を100%に戻すか、「測定の準備」をやり直してください。</Notice>}
          {fullscreenError && <Notice title="全画面表示を開始できませんでした。" tone="danger">ブラウザの全画面許可を確認してください。</Notice>}
          {environmentError && <Notice title="表示環境の再確認に合格しませんでした。" tone="danger">{environmentError}</Notice>}
          <button className="button session-start" type="button" disabled={!setupReady} onClick={begin}>{checkingEnvironment ? '画面の動きを確認しています…' : activeSession ? '全画面で続きから再開する' : '全画面で始める'}</button>
        </section>
      </div>
    </div>
  );
}
